'use client';

import { useState, useCallback, ChangeEvent } from 'react';
import RIPEMD160 from 'crypto-js/ripemd160';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Check, RefreshCw, Upload, FileText, Loader2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { ripemdHashContent } from '../../data/ripemd-hash-content';

type TabValue = 'text' | 'file';
type RipemdVariant = 'RIPEMD-128' | 'RIPEMD-160' | 'RIPEMD-256' | 'RIPEMD-320';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

const ripemdFunctions = {
    'RIPEMD-128': (data: string) => {
        // RIPEMD-128 is not available in crypto-js
        return 'RIPEMD-128 not available. Consider using RIPEMD-160.';
    },
    'RIPEMD-160': (data: string) => {
        return RIPEMD160(data).toString();
    },
    'RIPEMD-256': (data: string) => {
        // RIPEMD-256 is not available in crypto-js
        return 'RIPEMD-256 not available. Consider using RIPEMD-160.';
    },
    'RIPEMD-320': (data: string) => {
        // RIPEMD-320 is not available in crypto-js
        return 'RIPEMD-320 not available. Consider using RIPEMD-160.';
    },
};

export default function RipemdHashGenerator() {
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [variant, setVariant] = useState<RipemdVariant>('RIPEMD-160');
    const [textInput, setTextInput] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [hash, setHash] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();

    const generateHash = useCallback(async () => {
        if (activeTab === 'text' && !textInput.trim()) {
            toast({
                title: 'Error',
                description: 'Please enter some text to hash',
                variant: 'destructive',
            });
            return;
        }

        if (activeTab === 'file' && !file) {
            toast({
                title: 'Error',
                description: 'Please select a file to hash',
                variant: 'destructive',
            });
            return;
        }

        try {
            setIsLoading(true);
            let result = '';

            if (activeTab === 'text') {
                result = ripemdFunctions[variant](textInput);
            } else if (file) {
                const text = await file.text();
                result = ripemdFunctions[variant](text);
            }

            setHash(result);
            setIsCopied(false);
        } catch (error) {
            console.error('Error generating RIPEMD hash:', error);
            toast({
                title: 'Error',
                description: 'Failed to generate RIPEMD hash',
                variant: 'destructive',
            });
            setHash('');
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, textInput, file, variant, toast]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setFileInfo({
                name: selectedFile.name,
                size: selectedFile.size,
                type: selectedFile.type || 'Unknown',
            });
        }
    };

    const removeFile = () => {
        setFile(null);
        setFileInfo(null);
        setHash('');
    };

    const copyToClipboard = () => {
        if (!hash) return;

        navigator.clipboard.writeText(hash);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);

        toast({
            title: 'Copied!',
            description: 'RIPEMD hash has been copied to clipboard',
        });
    };

    const resetForm = () => {
        setTextInput('');
        setFile(null);
        setFileInfo(null);
        setHash('');
        setIsCopied(false);
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const getHashLength = () => {
        switch (variant) {
            case 'RIPEMD-128': return '128 bits (32 characters)';
            case 'RIPEMD-160': return '160 bits (40 characters)';
            case 'RIPEMD-256': return '256 bits (64 characters)';
            case 'RIPEMD-320': return '320 bits (80 characters)';
            default: return '';
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <ToolPageIntro title="RIPEMD Hash Generator">
                Generate RIPEMD hashes from text or files. Use for checksums and compatibility with systems that require RIPEMD. No sign-up required.
            </ToolPageIntro>

                <div className="space-y-2">
                    <Label>RIPEMD Variant</Label>
                    <Select
                        value={variant}
                        onValueChange={(value: RipemdVariant) => {
                            setVariant(value);
                            setHash(''); // Clear hash when variant changes
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-64">
                            <SelectValue placeholder="Select RIPEMD variant" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="RIPEMD-128">RIPEMD-128</SelectItem>
                            <SelectItem value="RIPEMD-160">RIPEMD-160 (most common)</SelectItem>
                            <SelectItem value="RIPEMD-256">RIPEMD-256</SelectItem>
                            <SelectItem value="RIPEMD-320">RIPEMD-320</SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground">
                        Output: {getHashLength()}
                    </p>
                </div>

                <Tabs
                    defaultValue="text"
                    className="w-full"
                    onValueChange={(value) => {
                        setActiveTab(value as TabValue);
                        resetForm();
                    }}
                >
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                        <TabsTrigger value="text">Text Input</TabsTrigger>
                        <TabsTrigger value="file">File Upload</TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from Text</CardTitle>
                                <CardDescription>
                                    Enter the text you want to generate a {variant} hash for
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="text-input">Input Text</Label>
                                    <Textarea
                                        id="text-input"
                                        value={textInput}
                                        onChange={(e) => setTextInput(e.target.value)}
                                        placeholder="Enter text to hash..."
                                        className="min-h-[120px] font-mono"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="file" className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Generate from File</CardTitle>
                                <CardDescription>
                                    Upload a file to generate its {variant} hash
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex items-center justify-center w-full">
                                        <label
                                            htmlFor="file-upload"
                                            className={cn(
                                                'flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer',
                                                'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700',
                                                'transition-colors duration-200 ease-in-out'
                                            )}
                                        >
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className="w-10 h-10 mb-3 text-gray-400" />
                                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    Any file type (Max: 10MB)
                                                </p>
                                            </div>
                                            <input
                                                id="file-upload"
                                                type="file"
                                                className="hidden"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                    </div>

                                    {fileInfo && (
                                        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <FileText className="h-5 w-5 text-gray-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {fileInfo.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {formatFileSize(fileInfo.size)} • {fileInfo.type || 'Unknown type'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={removeFile}
                                                    className="text-gray-400 hover:text-gray-500"
                                                >
                                                    <X className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2">
                    <Button
                        variant="outline"
                        onClick={resetForm}
                        disabled={isLoading || (!textInput && !file)}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reset
                    </Button>
                    <Button
                        onClick={generateHash}
                        disabled={isLoading || (activeTab === 'text' ? !textInput.trim() : !file)}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            `Generate ${variant} Hash`
                        )}
                    </Button>
                </div>

                {hash && (
                    <Card>
                        <CardHeader>
                            <CardTitle>{variant} Hash Result</CardTitle>
                            <CardDescription>
                                {activeTab === 'file' && fileInfo
                                    ? `${variant} hash for ${fileInfo.name}`
                                    : `Generated ${variant} hash`}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-2">
                                <div className="grid flex-1 gap-2">
                                    <Label htmlFor="hash-result" className="sr-only">
                                        {variant} Hash
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            id="hash-result"
                                            value={hash}
                                            readOnly
                                            className="font-mono"
                                        />
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="outline"
                                            className="h-10 w-10"
                                            onClick={copyToClipboard}
                                        >
                                            {isCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                            <span className="sr-only">Copy hash</span>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Hash length: {hash.length} characters ({hash.length * 4} bits)
                            </p>
                        </CardContent>
                    </Card>
                )}

                <Card className="border-dashed">
                    <CardHeader>
                        <CardTitle>About RIPEMD Hashes</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        <p>
                            <strong>RIPEMD</strong> (RACE Integrity Primitives Evaluation Message Digest) is a family of cryptographic hash functions developed in Leuven, Belgium, by Hans Dobbertin, Antoon Bosselaers, and Bart Preneel at the COSIC research group at the Katholieke Universiteit Leuven.
                        </p>
                        <p>
                            <strong>Common Variants:</strong>
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li><strong>RIPEMD-128:</strong> 128-bit hash function, designed as a drop-in replacement for MD4 and MD5</li>
                            <li><strong>RIPEMD-160:</strong> 160-bit hash function, the most commonly used variant, used in Bitcoin and other cryptocurrencies</li>
                            <li><strong>RIPEMD-256 & RIPEMD-320:</strong> Extended versions with 256-bit and 320-bit output respectively</li>
                        </ul>
                        <p className="text-xs text-muted-foreground/80">
                            Note: All hashing is performed in your browser. Your data never leaves your computer.
                        </p>
                    </CardContent>
                </Card>

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={ripemdHashContent.title}
                        description={ripemdHashContent.description}
                        content={ripemdHashContent.content}
                        faqs={ripemdHashContent.faqs}
                        relatedTools={ripemdHashContent.relatedTools}
                    />
                </div>
            </div>
            <Toaster />
        </div>
    );
}